import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useScroll } from '@/hooks/useScroll';
import { useUIStore } from '@/stores/uiStore';
import { useSiteConfig } from '@/stores/siteConfigStore';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from '@/components/i18n/LanguageSwitcher';
import ThemeToggle from '@/components/theme/ThemeToggle';
import { useContent } from '@/hooks/useContent';
import { usePortalText } from '@/portal/hooks/usePortalText';
import { isSessionActive, usePortalSession } from '@/portal/stores/portalSessionStore';
import AccountMenu from '@/portal/components/AccountMenu';
import { DOCTOR_ROOT_PATH, PORTAL_ROOT_PATH, PORTAL_LOGIN_PATH } from '@/portal/config';
import { roleFromToken } from '@/portal/utils/jwt';
import { clsx } from 'clsx';

const Header = () => {
  const { t } = useContent();
  const portal = usePortalText();
  const { isScrolled } = useScroll();
  const { isMenuOpen, toggleMenu } = useUIStore();
  const location = useLocation();
  const sessionToken = usePortalSession((state) => state.token);
  const sessionIssuedAt = usePortalSession((state) => state.issuedAt);
  const signedIn = isSessionActive({ token: sessionToken, issuedAt: sessionIssuedAt });
  const sessionRole = roleFromToken(sessionToken) ?? 'customer';
  // Login link / account menu only once the lab has said the login is on (hidden while unknown, so it never flashes).
  const loginEnabled = useSiteConfig((state) => state.loginEnabled);
  const loadSiteConfig = useSiteConfig((state) => state.load);
  useEffect(() => {
    void loadSiteConfig();
  }, [loadSiteConfig]);
  
  // Only transparent on home page hero section (when at top)
  const isHomePage = location.pathname === '/';
  const shouldBeTransparent = isHomePage && !isScrolled;

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path: string) =>
    clsx(
      'transition-colors whitespace-nowrap',
      isActive(path)
        ? 'text-brand-orange-base dark:text-brand-orange-base font-semibold'
        : shouldBeTransparent
          ? 'text-white dark:text-dark-text hover:text-brand-orange-base dark:hover:text-brand-orange-base'
          : 'text-light-text dark:text-dark-text hover:text-brand-orange-base dark:hover:text-brand-orange-base'
    );

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        shouldBeTransparent
          ? 'bg-transparent'
          : 'bg-light-primary dark:bg-dark-primary backdrop-blur-md shadow-lg'
      )}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/assets/images/elhamylab1024.jpeg"
              alt="ElhamyLabs"
              className="h-12 w-auto object-contain"
            />
            <span className="hidden xl:inline text-xl font-bold text-brand-orange-base whitespace-nowrap">
              {t('common.brandName')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 xl:gap-6">
            <Link to="/" className={navLinkClass('/')}>
              {t('common.home')}
            </Link>
            <Link to="/about" className={navLinkClass('/about')}>
              {t('common.about')}
            </Link>
            <Link to="/services" className={navLinkClass('/services')}>
              {t('common.services')}
            </Link>
            <Link to="/tests" className={navLinkClass('/tests')}>
              {t('common.tests')}
            </Link>
            <Link to="/branches" className={navLinkClass('/branches')}>
              {t('common.branches')}
            </Link>
            <Link to="/blog" className={navLinkClass('/blog')}>
              {t('common.blog')}
            </Link>
            <Link
              to="/contact"
              className={clsx(
                'px-4 py-2 rounded-lg transition-colors whitespace-nowrap',
                isActive('/contact')
                  ? 'bg-brand-purple-base dark:bg-brand-orange-base text-white'
                  : 'bg-brand-orange-base text-white hover:bg-brand-orange-light'
              )}
            >
              {t('common.contact')}
            </Link>
            {loginEnabled !== true ? null : signedIn ? (
              <AccountMenu overHero={shouldBeTransparent} />
            ) : (
              <Link
                to={PORTAL_LOGIN_PATH}
                className="px-4 py-2 rounded-lg border-2 border-brand-orange-base text-brand-orange-base font-semibold hover:bg-brand-orange-base hover:text-white transition-colors"
              >
                {portal.t('navLogin')}
              </Link>
            )}
            <LanguageSwitcher />
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className={clsx(
              'md:hidden p-2 transition-colors',
              shouldBeTransparent
                ? 'text-white dark:text-dark-text'
                : 'text-light-text dark:text-dark-text'
            )}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className={clsx(
            'md:hidden mt-4 pb-4 border-t',
            shouldBeTransparent
              ? 'border-white/20 dark:border-dark-text/10'
              : 'border-light-text/10 dark:border-dark-text/10'
          )}>
            <div className="flex flex-col gap-4 pt-4">
              <Link
                to="/"
                onClick={toggleMenu}
                className={navLinkClass('/')}
              >
                {t('common.home')}
              </Link>
              <Link
                to="/about"
                onClick={toggleMenu}
                className={navLinkClass('/about')}
              >
                {t('common.about')}
              </Link>
              <Link
                to="/services"
                onClick={toggleMenu}
                className={navLinkClass('/services')}
              >
                {t('common.services')}
              </Link>
              <Link
                to="/tests"
                onClick={toggleMenu}
                className={navLinkClass('/tests')}
              >
                {t('common.tests')}
              </Link>
              <Link
                to="/branches"
                onClick={toggleMenu}
                className={navLinkClass('/branches')}
              >
                {t('common.branches')}
              </Link>
              <Link
                to="/blog"
                onClick={toggleMenu}
                className={navLinkClass('/blog')}
              >
                {t('common.blog')}
              </Link>
              <Link
                to="/contact"
                onClick={toggleMenu}
                className={clsx(
                  'px-4 py-2 rounded-lg transition-colors text-center',
                  isActive('/contact')
                    ? 'bg-brand-purple-base dark:bg-brand-orange-base text-white'
                    : 'bg-brand-orange-base text-white hover:bg-brand-orange-light'
                )}
              >
                {t('common.contact')}
              </Link>
              {loginEnabled !== true ? null : signedIn ? (
                <Link
                  to={`${sessionRole === 'doctor' ? DOCTOR_ROOT_PATH : PORTAL_ROOT_PATH}/results`}
                  onClick={toggleMenu}
                  className={navLinkClass(`${sessionRole === 'doctor' ? DOCTOR_ROOT_PATH : PORTAL_ROOT_PATH}/results`)}
                >
                  {portal.t(sessionRole === 'doctor' ? 'drNavResults' : 'navResults')}
                </Link>
              ) : (
                <Link
                  to={PORTAL_LOGIN_PATH}
                  onClick={toggleMenu}
                  className="px-4 py-2 rounded-lg border-2 border-brand-orange-base text-brand-orange-base font-semibold text-center"
                >
                  {portal.t('navLogin')}
                </Link>
              )}
              <div className="flex items-center gap-4 pt-2">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
